import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import _ from "lodash";

import { alertData } from "./../../states/alert";
import { dashboardCardsData } from "./../../states/dashboardCardsData";
import { personalData } from "./../../states/personalData";
import { logEvent, logPageView } from "./../../analytics";
import { EVENTS } from "./../../constants/ga-constants";
import "./StudentCardDetails.css";
import axios from "../../axios";

const formatTimeTo12Hr = (timeStr) => {
  if (!timeStr || timeStr === "-") return "-";
  const parts = timeStr.split(":");
  if (parts.length < 2) return timeStr;
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1];
  if (isNaN(hours)) return timeStr;
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const paddedHours = String(hours).padStart(2, "0");
  return `${paddedHours}:${minutes} ${ampm}`;
};

const formatDate = (dateStr) => {
  if (!dateStr || dateStr === "-") return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = d.getDate();
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr || dateStr === "-") return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${day}/${month}/${year} at ${hours}:${minutes}${ampm}`;
};

const StudentCardDetailsPopup = ({ cardId, onClose }) => {
  const [cardsCenteralData, setCardsCenteralData] = useAtom(dashboardCardsData);
  const [personalCenteralData, setPersonalCenteralData] = useAtom(personalData);
  const [currentCard, setCurrentCard] = useState();

  const [alert, setAlert] = useAtom(alertData);
  const [loading, setLoading] = useState(false);

  const navigation = useNavigate();

  const updateCentralData = (newCentralData) => {
    let updatedCurrentData = { ...currentCard, ...newCentralData };
    setCurrentCard(() => updatedCurrentData);
  };

  const updateTutorDemoInterest = (payload) => {
    const newStatus = payload.isInterested ? "INTERESTED" : "NOT_INTERESTED";
    updateCentralData({
      isActive: true,
      isInterested: payload.isInterested,
      status: newStatus,
    });
    setCardsCenteralData((currentData) =>
      currentData.map((card) =>
        card.dealings_id === currentCard.dealings_id ? { ...card, status: newStatus, isActive: true } : card
      )
    );

    axios
      .post("/v1/tutor/demo-update-interest", {
        dealings_id: currentCard.dealings_id,
        isInterested: payload.isInterested,
      })
      .then((response) => {
        if (response.data.is_success) {
          if (response.data.data) {
            const currentDemoShortlistClass = response.data.data;
            const updatedStatus = currentDemoShortlistClass.status || newStatus;
            updateCentralData({
              isActive: true,
              isInterested: payload.isInterested,
              status: updatedStatus,
              ...response.data,
            });
            setCardsCenteralData((currentData) =>
              currentData.map((card) =>
                card.dealings_id === currentDemoShortlistClass.dealID
                  ? { ...card, status: updatedStatus }
                  : card
              )
            );
          }
          setAlert("Your interest has been recorded.");
        }
      })
      .catch((err) => {
        setAlert("Failed to update interest. Please try again.");
      });
  };

  useEffect(() => {
    const card = cardsCenteralData.find((card) => card.dealings_id === cardId);
    if (card) {
      setCurrentCard(card);
    } else if (cardId) {
      setLoading(true);
      axios
        .get(`/v1/tutor/dashboard-card-details?dealingsId=${cardId}`)
        .then((res) => {
          if (res.data.is_success) {
            setCurrentCard(res.data.data);
          }
        })
        .catch((err) => {
          console.error("Error fetching card details:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [cardId, cardsCenteralData]);

  const postAppliedJobs = () => {
    axios
      .post("/v1/tutor/applied-jobs", {
        dealings_id: currentCard.dealings_id,
        distance: currentCard.distance,
      })
      .then((response) => {
        if (response.data.is_success) {
          setCardsCenteralData((currentDate) => {
            const AppliedJobIds = response.data.data;
            const newData = currentDate.map((card) => {
              if (AppliedJobIds.indexOf(parseInt(card.dealings_id)) !== -1) return { ...card, isActive: true };
              return card;
            });
            return newData;
          });

          if (!personalCenteralData.is_paid) {
            logEvent(EVENTS.UNVERIFIED_TUTOR_JOB.event_name);
            navigation("/verify-account");
          } else logEvent(EVENTS.VERIFIED_TUTOR_JOB.event_name);
          setAlert("Successfully Applied, Our CRE will reach out to you soon. ");
        }
      })
      .catch((err) => {
        // console.log("error while appling to job ");
      });
  };

  if (loading) {
    return (
      <div className="popup-overlay">
        <div className="popup-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <div className="loading">
            <img src="./book-loader.gif" alt="Loading..." />
          </div>
        </div>
      </div>
    );
  }

  if (!currentCard) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-container">
        <button className="popup-close" onClick={onClose}>
          ×
        </button>

        {/* Top Status */}
        <div className="popup-top">
          {currentCard.status && currentCard.card_domain !== "RECOMMENDATION" && (
            <div className="popup-status">
              {(() => {
                const statusColorMap = {
                  YET_TO_SCHEDULE: "rgba(255, 153, 0, 1)",
                  ASSIGNED: "rgba(35, 139, 139, 1)",
                  INTERESTED: "rgba(33, 150, 243, 1)",
                  NOT_INTERESTED: "rgba(220, 53, 69, 1)",
                  IN_PROGRESS: "rgba(255, 153, 0, 1)",
                  DEMO_SCHEDULED: "rgba(239, 176, 37, 1)",
                  CONFIRMED: "rgba(40, 167, 69, 1)",
                  CANCELLED: "rgba(108, 117, 125, 1)",
                  REJECTED: "rgba(220, 53, 69, 1)",
                  REMOVED: "rgba(108, 117, 125, 1)",
                };

                const statusEffectMap = {
                  YET_TO_SCHEDULE: {
                    boxShadow: "0 0 9px rgba(255, 153, 0, 1)",
                    backgroundColor: "rgba(255, 153, 0, 1)",
                  },
                  ASSIGNED: {
                    boxShadow: "0 0 9px rgba(35, 139, 139, 1)",
                    backgroundColor: "rgba(35, 139, 139, 1)",
                  },
                  INTERESTED: {
                    boxShadow: "0 0 9px rgba(33, 150, 243, 1)",
                    backgroundColor: "rgba(33, 150, 243, 1)",
                  },
                  NOT_INTERESTED: {
                    boxShadow: "0 0 9px rgba(220, 53, 69, 1)",
                    backgroundColor: "rgba(220, 53, 69, 1)",
                  },
                  IN_PROGRESS: {
                    boxShadow: "0 0 9px rgba(255, 153, 0, 1)",
                    backgroundColor: "rgba(255, 153, 0, 1)",
                  },
                  DEMO_SCHEDULED: {
                    boxShadow: "0 0 9px rgba(239, 176, 37, 1)",
                    backgroundColor: "rgba(239, 176, 37, 1)",
                  },
                  CONFIRMED: {
                    boxShadow: "0 0 9px rgba(40, 167, 69, 1)",
                    backgroundColor: "rgba(40, 167, 69, 1)",
                  },
                  CANCELLED: {
                    boxShadow: "0 0 9px rgba(108, 117, 125, 1)",
                    backgroundColor: "rgba(108, 117, 125, 1)",
                  },
                  REJECTED: {
                    boxShadow: "0 0 9px rgba(220, 53, 69, 1)",
                    backgroundColor: "rgba(220, 53, 69, 1)",
                  },
                  REMOVED: {
                    boxShadow: "0 0 9px rgba(108, 117, 125, 1)",
                    backgroundColor: "rgba(108, 117, 125, 1)",
                  },
                };

                const normalizedStatus = currentCard.status ?? "YET_TO_SCHEDULE";

                return (
                  <>
                    Status:{" "}
                    <span
                      style={{
                        color: statusColorMap[normalizedStatus],
                      }}
                    >
                      {_.startCase(_.toLower(currentCard.status))}
                    </span>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="popup-content">
          {/* Left Section */}

          <div className="left-section">
            <div className="tutor-student-card-details-name">Class Details</div>

            <div className="tutor-student-card-details-items-container">
              <div className="tutor-student-card-details-item">
                <img src="./card-person-icon.svg" alt="card-person-icon" /> {currentCard.student_name}
              </div>
              <div className="tutor-student-card-details-item">
                <img src="./card-subject-icon.svg" alt="card-subject-icon" /> Subjects : {currentCard.subjects}
              </div>
              <div className="tutor-student-card-details-item">
                <img src="./card-subject-icon.svg" alt="card-address-icon" /> Level : {currentCard.level} ,{" "}
                {currentCard.stream}
              </div>
              <div className="tutor-student-card-details-item">
                <img src="./card-subject-icon.svg" alt="card-address-icon" /> Classes/Week :{" "}
                {currentCard.days_per_week}
              </div>
              <div className="tutor-student-card-details-item">
                <img src="./card-address-icon.svg" alt="card-address-icon" /> Distance :{" "}
                {currentCard.distance !== undefined && currentCard.distance !== null ? currentCard.distance.toFixed(1) : "-"} Km away
              </div>
              {currentCard.address !== "undefined" && currentCard.address && (
                <div className="tutor-student-card-details-item">
                  <img src="./card-address-icon.svg" alt="card-address-icon" /> Address :{" "}
                  {currentCard.address.split(",").map((val, ind, arr) => (ind < 3 ? val : ""))}
                </div>
              )}
              {(currentCard.address === "undefined" || !currentCard.address) && (
                <div className="tutor-student-card-details-item">
                  <img src="./card-address-icon.svg" alt="card-address-icon" /> Address {currentCard.zip}
                </div>
              )}
              {currentCard.amount && (
                <div className="tutor-student-card-details-item">
                  <img src="./amount-icon.svg" alt="card-address-icon" />Fees : ₹{currentCard.amount}/month
                </div>
              )}
            </div>

            <div className="section-midline-hr"></div>

            <div className="special-requirement">
              <div className="tutor-student-card-details-space-between-flex">
                <div className="tutor-student-card-details-name">Notes / Special Requirements</div>
                <p>{currentCard.requirement_notes || currentCard.note || "No special requirements mentioned."}</p>
              </div>
            </div>

            <div className="section-midline-hr"></div>

            <div className="special-requirement">
              <div className="tutor-student-card-details-space-between-flex">
                <div style={{ width: "100%" }}>
                  <div className="tutor-student-card-details-name" style={{ marginBottom: "6px" }}>
                    Additional Class Details
                  </div>
                  <div className="tutor-student-card-details-item" style={{ fontSize: "14px", marginTop: "4px" }}>
                    Assigned: {formatDate(currentCard.assigned_date || currentCard.created_at || currentCard.assignedAt) || "14/2/2023"}
                  </div>
                  {(["DEMO_SCHEDULED", "CONFIRMED", "CANCELLED"].includes(currentCard.status) || currentCard.demo_date) && (
                    <div className="tutor-student-card-details-item" style={{ fontSize: "14px", marginTop: "4px", color: currentCard.status === "CANCELLED" ? "rgba(154, 154, 154, 1)" : "inherit" }}>
                      Demo Date: {formatDate(currentCard.demo_date) || formatDate(currentCard.assigned_date || currentCard.created_at) || "14/2/2023"}
                    </div>
                  )}
                  {(currentCard.status === "CONFIRMED" || currentCard.start_date || currentCard.confirmed_tuition_date) && (
                    <div className="tutor-student-card-details-item" style={{ fontSize: "14px", marginTop: "4px" }}>
                      Start Date: {formatDate(currentCard.start_date || currentCard.confirmed_tuition_date) || formatDate(currentCard.assigned_date || currentCard.created_at) || "14/2/2023"}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* <div className="section-midline-hr"></div> */}
          </div>

          {/* Right Section */}
          <div className="right-section">
            {(currentCard.card_domain === "DEMO_CLASSES" ||
              currentCard.card_domain === "MY_CLASSES" ||
              currentCard.is_hotlead) && (
                <>
                  <div className="tutor-student-card-details-space-between-flex">
                    <div className="tutor-student-card-details-name">Address & Contact Info</div>
                  </div>
                  <div className="tutor-student-card-details-space-between-flex">
                    <div
                      class="container"
                      style={{
                        alignItems: "center",
                      }}
                    >
                      <div
                        class="item-1"
                        style={{
                          width: "50%",
                        }}
                      >
                        <div style={{ fontWeight: "400", fontSize: "14px", lineHeight: "120%" }}>Call Student</div>

                        <div className="tutor-student-card-details-call-to-action-icon">
                          {(currentCard.status === "ASSIGNED" ||
                            currentCard.status === "DEMO_SCHEDULED" ||
                            currentCard.card_domain === "MY_CLASSES") &&
                            currentCard.phone ? (
                            <a href={`tel:${currentCard.phone}`}>
                              <img src="./phone-dial-icon.svg" alt="call-to-action-icon" />
                            </a>
                          ) : (
                            <a>
                              <img src="./phone-dial-icon-disabled.svg" alt="call-to-action-icon" />
                            </a>
                          )}
                        </div>
                      </div>
                      <div
                        class="item-2"
                        style={{
                          width: "50%",
                        }}
                      >
                        <div style={{ fontWeight: "400", fontSize: "14px", lineHeight: "120%" }}>
                          Tap to view location
                        </div>

                        <div className="tutor-student-card-details-call-to-action-icon">
                          {currentCard.location && currentCard.status !== "NOT_INTERESTED" ? (
                            <a href={currentCard.location} target="_blank" rel="noreferrer">
                              <img src="./location.svg" alt="call-to-action-icon" />
                            </a>
                          ) : (
                            <a>
                              <img src="./location-disabled.svg" alt="call-to-action-icon" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="section-midline-hr"></div>
                </>
              )}

            <div className="timing-schedule">
              <div className="tutor-student-card-details-space-between-flex">
                <div className="tutor-student-card-details-name">Timing & Schedule</div>
              </div>
              <div className="timing-schedule-blocks" style={{ padding: "10px 0" }}>
                <div className="tutor-student-card-details-space-between-flex">
                  <div className="tutor-student-card-details-item">No of Classes/Week :- </div>
                </div>
                <div className="tutor-student-card-details-space-between-flex">
                  <div className="tutor-student-card-details-item">{currentCard.days_per_week}</div>
                </div>
              </div>

              <div className="timing-schedule-blocks" style={{ padding: "10px 0" }}>
                <div className="tutor-student-card-details-space-between-flex">
                  <div className="tutor-student-card-details-item">Tuition Timing Between : </div>
                </div>
                <div className="tutor-student-card-details-space-between-flex">
                  <div className="tutor-student-card-details-item">
                    {formatTimeTo12Hr(currentCard.tuition_start_time)} to {formatTimeTo12Hr(currentCard.tuition_end_time)}
                  </div>
                </div>
              </div>

              <div className="timing-schedule-blocks" style={{ padding: "10px 0" }}>
                <div className="tutor-student-card-details-space-between-flex">
                  <div className="tutor-student-card-details-item">No. of Teaching Hours : </div>
                </div>
                <div className="tutor-student-card-details-space-between-flex">
                  <div className="tutor-student-card-details-item">{currentCard.tuition_hours ?? "-"} hrs/day</div>
                </div>
              </div>

              {["ASSIGNED"].includes(currentCard.status) && (
                <div style={{ padding: "10px 0" }}>
                  <div className="tutor-student-card-details-space-between-flex">
                    <div
                      className="tutor-student-card-details-item"
                      style={{
                        color: "rgba(39, 65, 120, 1)",
                        padding: "10px 10px 10px 0",
                      }}
                    >
                      Call Student/Parent to Schedule demo class
                    </div>
                  </div>
                </div>
              )}
              {["INTERESTED"].includes(currentCard.status) && (
                <div style={{ padding: "10px 0" }}>
                  <div className="tutor-student-card-details-space-between-flex">
                    <div
                      className="tutor-student-card-details-item"
                      style={{
                        color: "rgba(39, 65, 120, 1)",
                        fontWeight: 600,
                        padding: "10px 10px 10px 0",
                      }}
                    >
                      Your request is being processed. You will be notified Shortly
                    </div>
                  </div>
                </div>
              )}
              {currentCard.status === "DEMO_SCHEDULED" && (
                <div style={{ padding: "10px 0", textAlign: "right" }}>
                  <div className="tutor-student-card-details-space-between-flex" style={{ justifyContent: "flex-end" }}>
                    <div
                      className="tutor-student-card-details-item"
                      style={{
                        color: "rgba(39, 65, 120, 1)",
                        fontWeight: 600,
                        fontSize: "15px",
                      }}
                    >
                      Demo Date & Time: {formatDateTime(currentCard.demo_date) || "12/12/2023 at 6:30PM"}
                    </div>
                  </div>
                </div>
              )}
              {currentCard.status === "CONFIRMED" && (
                <div style={{ padding: "10px 0", textAlign: "right" }}>
                  <div className="tutor-student-card-details-space-between-flex" style={{ justifyContent: "flex-end" }}>
                    <div
                      className="tutor-student-card-details-item"
                      style={{
                        color: "rgba(39, 65, 120, 1)",
                        fontWeight: 600,
                        fontSize: "15px",
                      }}
                    >
                      Starting Date: {formatDate(currentCard.start_date || currentCard.confirmed_tuition_date) || "12/12/2023"}
                    </div>
                  </div>
                </div>
              )}
              {currentCard.status === "CANCELLED" && (
                <div style={{ padding: "10px 0", textAlign: "right" }}>
                  <div className="tutor-student-card-details-space-between-flex" style={{ justifyContent: "flex-end" }}>
                    <div
                      className="tutor-student-card-details-item"
                      style={{
                        color: "rgba(39, 65, 120, 1)",
                        fontWeight: 600,
                        fontSize: "15px",
                      }}
                    >
                      Cancelled Date: {formatDate(currentCard.cancelled_date || currentCard.updated_at) || "12/12/2023"}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {currentCard.card_domain === "RECOMMENDATION" && (
              <div>
                {currentCard.isActive ? (
                  <div
                    className="popup-apply"
                    style={{
                      backgroundColor: "rgb(234, 185, 9)",
                      color: "black",
                    }}
                  >
                    <div
                      className={`tutor-student-card-details-right-content-2--btn`}
                      style={{
                        backgroundColor: "rgb(234, 185, 9)",
                        color: "black",
                        cursor: "auto",
                      }}
                    >
                      Applied
                    </div>
                  </div>
                ) : (
                  <div
                    className="popup-apply"
                    style={{
                      backgroundColor: "rgba(39, 65, 120, 1)",
                      color: "white",
                    }}
                  >
                    <div
                      className="tutor-student-card-details-right-content-2--btn"
                      style={{
                        backgroundColor: "rgba(39, 65, 120, 1)",
                        color: "white",
                      }}
                      onClick={() => {
                        updateCentralData({ isActive: true });
                        postAppliedJobs();
                        onClose();
                      }}
                    >
                      Apply
                    </div>
                  </div>
                )}
              </div>
            )}

            {currentCard.card_domain === "DEMO_CLASSES" && currentCard.status === "YET_TO_SCHEDULE" && (
              <div className="demo-class-interested">
                <div
                  style={{
                    color: "rgba(39, 65, 120, 1)",
                    fontSize: "20px",
                    fontWeight: 500,
                    lineHeight: "120%",
                  }}
                >
                  Are You Interested to go for a demo class?
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-around",
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <div
                    className="tutor-student-card-right-interested-demo-button"
                    style={
                      currentCard.status === "INTERESTED"
                        ? { backgroundColor: "rgba(39, 65, 120, 1)", color: "white" }
                        : currentCard.status === "NOT_INTERESTED"
                          ? {
                            backgroundColor: "rgba(217, 217, 217, 1)",
                            color: "white",
                            border: "none",
                          }
                          : {}
                    }
                    onClick={
                      ["INTERESTED", "NOT_INTERESTED"].includes(currentCard.status)
                        ? () => {
                          console.log("not clickable");
                        }
                        : () => {
                          updateTutorDemoInterest({ isInterested: true });
                          postAppliedJobs();
                        }
                    }
                  >
                    Yes
                  </div>
                  <div
                    className="tutor-student-card-right-interested-demo-button"
                    style={
                      currentCard.status === "NOT_INTERESTED"
                        ? { backgroundColor: "rgba(39, 65, 120, 1)", color: "white" }
                        : {}
                    }
                    onClick={
                      currentCard.status === "ASSIGNED" || currentCard.status === "YET_TO_SCHEDULE"
                        ? () => {
                          updateTutorDemoInterest({ isInterested: false });
                          postAppliedJobs();
                        }
                        : () => {
                          console.log("not clickable");
                        }
                    }
                  >
                    No
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentCardDetailsPopup;
